<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <v-toolbar
                    id="layer-toolbar"
                    flat
                >
                    <v-row>
                        <v-col
                            cols="6"
                        >
                            <v-toolbar-title>
                                Map Layers
                            </v-toolbar-title>
                        </v-col>
                        <v-col
                            cols="6"
                            class="text-right"
                        >
                            <v-btn
                                class="primary"
                                @click="editLayer()"
                            >
                                <v-icon
                                    class="pr-2"
                                >
                                    mdi-plus
                                </v-icon>
                                Add layer
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-toolbar>
            </v-col>
        </v-row>
        <v-row
            class="mt-0"
            :no-gutters="this.$vuetify.breakpoint.xs"
        >
            <v-col>
                <v-data-table
                    id="layer-table"
                    fixed-header
                    :headers="headers"
                    :items="companyLayers"
                    item-key="layer_id"
                    :items-per-page="itemsPerPage"
                    dense
                >
                    <template v-slot:[`item.polygons`]="{item}">
                        {{ item.external_layer_json.length ? '--' : item.polygons }}
                    </template>
                    <template v-slot:[`item.counties`]="{item}">
                        {{ item.external_layer_json.length ? '--' : item.counties }}
                    </template>
                    <template v-slot:[`item.created`]="{item}">
                        {{ formatDate(item.created) }}
                    </template>
                    <template v-slot:[`item.updated`]="{item}">
                        {{ formatDate(item.updated) }}
                    </template>
                    <template v-slot:[`item.import_status`]="{ item }">
                        <v-tooltip
                            v-if="item.import_status === 'LOADING'"
                            class="tooltip"
                            bottom
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    class="warning--text"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="importStatus(item.import_log)"
                                >
                                    mdi-dots-horizontal-circle-outline
                                </v-icon>
                            </template>
                            <span>{{ item.import_status }}</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="item.import_status === 'SUCCESS'"
                            class="tooltip"
                            bottom
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    class="success--text"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="importStatus(item.import_log)"
                                >
                                    mdi-check-circle-outline
                                </v-icon>
                            </template>
                            <span>{{ item.import_status }}</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="item.import_status === 'ERROR'"
                            class="tooltip"
                            bottom
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    class="error--text"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="importStatus(item.import_log)"
                                >
                                    mdi-alert-circle-outline
                                </v-icon>
                            </template>
                            <span>{{ item.import_status }}</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="item.external_layer_json !== ''"
                            class="tooltip"
                            bottom
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    class="warning--text"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-link
                                </v-icon>
                            </template>
                            <span>EXTERNAL LAYER</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`item.show`]="{ item }">
                        <label>{{ getShow(item.style) }}</label>
                    </template>
                    <template v-slot:[`item.color`]="{ item }">
                        <v-icon :color="getColor(item.style)">
                            mdi-circle
                        </v-icon>
                    </template>
                    <template v-slot:[`item.draw`]="{ item }">
                        <v-icon
                            v-if="item.external_layer_json === ''"
                            class="icon-pointer"
                            @click="drawLayer(item.layer_id)"
                        >
                            mdi-draw
                        </v-icon>
                        <v-icon
                            v-else
                        >
                            mdi-link
                        </v-icon>                        
                    </template>
                    <template v-slot:[`item.upload`]="{ item }">
                        <v-icon
                            v-if="item.external_layer_json === ''"
                            class="icon-pointer"
                            @click="uploadLayer(item.layer_id)"
                        >
                            mdi-upload
                        </v-icon>
                        <v-icon
                            v-else
                        >
                            mdi-link
                        </v-icon>                        
                    </template>
                    <template v-slot:[`item.edit`]="{ item }">
                        <v-icon
                            @click="editLayer(item.layer_id)"
                        >
                            mdi-pencil
                        </v-icon>
                    </template>
                    <template v-slot:[`item.delete`]="{ item }">
                        <v-icon
                            small
                            @click="deleteLayerDialog(item.layer_id)"
                        >
                            mdi-delete
                        </v-icon>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <!-- Edit Layer Dialog -->
        <v-dialog
            v-model="mapLayerPropertyDialog.visible"
            persistent
            max-width="500px"
        >
            <v-form
                ref="editDialogForm"
                v-model="mapLayerPropertyDialog.validated"
            >
                <v-card>
                    <v-card-title class="text-h5">
                        {{ mapLayerPropertyDialog.mapLayer.layer_id.length ? 'Edit' : 'Add' }} Map Layer
                    </v-card-title>
                    <v-divider />

                    <v-card-text>
                        <v-checkbox
                            v-model="mapLayerPropertyDialog.mapLayer.style.show"
                            label="Viewable by users as a layer in Ticket Details"
                        />
                        <v-text-field
                            ref="shortName"
                            v-model="mapLayerPropertyDialog.mapLayer.name"
                            label="Short Name"
                            required
                            :rules="rules.nameRules"
                            outlined
                        />
                        <v-text-field
                            v-model="mapLayerPropertyDialog.mapLayer.description"
                            label="Description"
                            :rules="rules.nameRules"
                            required
                            outlined
                        />
                        <v-toolbar flat>
                            <v-checkbox
                                v-model="mapLayerPropertyDialog.mapLayer.addESRILayer"
                                label="ESRI Feature Layer"
                            />
                            <v-spacer />
                            <v-progress-linear
                            v-if="mapLayerPropertyDialog.mapLayer.isEsriValidationActive"
                                indeterminate
                                height="10"
                                striped
                                color="lime"
                                style="width: 120px;"
                            ></v-progress-linear>
                            <v-icon
                                v-if="mapLayerPropertyDialog.mapLayer.isEsriValidationActive"
                                class="mx-2"
                                x-large
                            >
                                foggy
                            </v-icon>
                            <v-icon
                                v-if="mapLayerPropertyDialog.mapLayer.addESRILayer && mapLayerPropertyDialog.mapLayer.addESRILayerValidated"
                                class="mx-2"
                                x-large
                                color="success"
                            >
                                cloud_done
                            </v-icon>
                            <v-btn
                                v-if="showEsriValidateButton"
                                ref="validateEsriLayerButton"
                                color="secondary"
                                @click="validateEsriLayer()"
                            >
                                VALIDATE
                            </v-btn>
                        </v-toolbar>
                        <v-card-text
                            v-if="mapLayerPropertyDialog.mapLayer.addESRILayer && mapLayerPropertyDialog.mapLayer.external_layer_json.message.length"
                        >
                            <v-alert
                                dismissible
                                type="error"
                            >
                                {{ mapLayerPropertyDialog.mapLayer.external_layer_json.message }}
                            </v-alert>
                        </v-card-text>
                        <div v-if="mapLayerPropertyDialog.mapLayer.addESRILayer">
                            <v-text-field
                                v-model="mapLayerPropertyDialog.mapLayer.external_layer_json.url"
                                label="ESRI URL"
                                :rules="[rules.required, rules.url]"
                                @change="resetEsriValidation()"
                                outlined
                            />
                            <v-text-field
                                v-model="mapLayerPropertyDialog.mapLayer.external_layer_json.apiKey"
                                label="ESRI API Key"
                                :rules="[rules.noSpaces]"
                                @keydown="resetEsriValidation()"
                                outlined
                            />
                            <v-select
                                v-if="mapLayerPropertyDialog.mapLayer.addESRILayerValidated || (mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields && mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields.length)"
                                v-model="mapLayerPropertyDialog.mapLayer.external_layer_json.label"
                                :items="mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields"
                                label="Label Field"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                            />
                        </div>
                        <template>
                            Color:
                            <v-menu
                                v-model="menu"
                                offset-y
                                :close-on-content-click="false"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-icon
                                        :color="mapLayerPropertyDialog.mapLayer.style.color"
                                        label="Color"
                                        v-on="on"
                                    >
                                        mdi-circle
                                    </v-icon>
                                </template>
                                <v-color-picker
                                    v-model="mapLayerPropertyDialog.mapLayer.style.color"
                                    hide-inputs
                                    hide-swatches
                                />
                            </v-menu>
                        </template>
                        <v-card-actions>
                            <v-spacer />
                            <v-btn
                                depressed
                                @click="cancelEditLayer()"
                            >
                                Cancel
                            </v-btn>

                            <v-btn
                                depressed
                                color="primary"
                                :disabled="isEditDialogSaveDisabled"
                                @click="UpdateLayer()"
                            >
                                Save
                            </v-btn>
                        </v-card-actions>
                    </v-card-text>
                </v-card>
            </v-form>
        </v-dialog>
        <!-- Delete Layer Dialog -->
        <v-dialog
            v-model="mapLayerDeleteDialog.visible"
            persistent
            max-width="500px"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Delete Layer
                </v-card-title>

                <v-card-text>
                    Are you sure you want to delete '{{ mapLayerDeleteDialog.name }}' from the map layers lists?
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        depressed
                        @click="cancelDeleteLayer()"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        depressed
                        color="primary"
                        @click="deleteLayer()"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Import Staus Dialog -->
        <v-dialog
            v-model="importStatusDialog"
            persistent
            max-width="800px"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Import Status
                    <v-spacer />
                    <v-btn
                        icon
                        @click="closeStatusDialog()"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-divider />

                <v-card-text>
                    <div class="pre">
                        {{ importStatusText }}
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        depressed
                        color="primary"
                        @click="closeStatusDialog()"
                    >
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import webtmsBackend from '../../../services/webtmsBackend';
import moment from 'moment';
import { guidEqual } from '@/utils/guid.js';

const GET_COMPANY_MAP_LAYER_INTERVAL = 15000;

export default {
    name: 'MapLayers',
    components: {

    },
    data: () => ({
        refreshCompanyLayers: true,
        dialog: false,
        dialogDelete: false,
        importStatusDialog: false,
        importStatusText: '',
        itemsPerPage: 15,
        headers: [
            {
                text: 'Name',
                align: 'left',
                sortable: false,
                value: 'name',
            },
            {
                text: 'Description',
                align: 'left',
                sortable: false,
                value: 'description',
            },
            {
                text: 'Polygons',
                align: 'left',
                sortable: false,
                value: 'polygons',
            },
            {
                text: 'Counties',
                align: 'left',
                sortable: false,
                value: 'counties',
            },
            {
                text: 'Import Status',
                align: 'center',
                sortable: false,
                value: 'import_status',
            },
            {
                text: 'Created',
                align: 'left',
                sortable: false,
                value: 'created',
            },
            {
                text: 'By',
                align: 'left',
                sortable: false,
                value: 'created_by',
            },
            {
                text: 'Updated',
                align: 'left',
                sortable: false,
                value: 'updated',
            },
            {
                text: 'Updated By',
                align: 'left',
                sortable: false,
                value: 'updated_by',
            },
            {
                text: 'Viewable',
                align: 'left',
                sortable: false,
                value: 'show',
            },
            {
                text: 'Color',
                align: 'center',
                sortable: false,
                value: 'color',
            },
            {
                text: 'Draw',
                align: 'center',
                sortable: false,
                value: 'draw',
            },
            {
                text: 'Upload',
                align: 'center',
                value: 'upload',
                sortable: false,
            },
            {
                text: 'Edit',
                align: 'center',
                value: 'edit',
                sortable: false,
            },
            {
                text: 'Delete',
                align: 'center',
                value: 'delete',
                sortable: false,
            }
        ],
        companyLayers: [],
        mapLayerPropertyDialog: {
            visible: false,
            validated: false,
            mapLayer: {
                layer_id: '',
                name:'',
                description:'',
                style: { show:false, color:'#808080' },
                addESRILayer: false,
                addESRILayerValidated: false,
                isEsriValidationActive: false,
                external_layer_json: { type: 'esri-feature', url: '', apiKey: '', label: '', message: '', labelFields: [{text: '(Do not label)', value: ''}] }
            }
        },
        mapLayerDeleteDialog: {
            visible: false,
            name: '',
            layer_id: ''
        },
        menu: false,
        rules: {
            required: value => !!value || 'This field is required.',
            nameRules: [
                v => !!v || 'This field is required',
                v => (v && v.length >= 3) || 'Must be at least 3 characters.',
            ],
            url: value => {
                const pattern = /^https:\/\/[a-zA-Z0-9.-]+\/[a-zA-Z0-9/_.-]+$/;
                return pattern.test(value);
            },
            layerFieldName: value => {
                const pattern = /(^[A-Za-z0-9_]+|(^$))$/;
                return pattern.test(value);
            },
            noSpaces: value => {
                const pattern = /^\S*$/;
                return pattern.test(value);
            }
        },
    }),
    computed: {
        showEsriValidateButton() {
            return this.mapLayerPropertyDialog.validated 
                && this.mapLayerPropertyDialog.mapLayer.addESRILayer 
                && !this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated 
                && !this.mapLayerPropertyDialog.mapLayer.isEsriValidationActive
                ;
        },
        isEditDialogSaveDisabled() {
            if(!this.mapLayerPropertyDialog.validated) return true;
            if(this.mapLayerPropertyDialog.mapLayer.addESRILayer) {
                return !this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated;
            }
            return false;
        }
    },
    created() {
        this.refreshCompanyLayers = true;
        this.getCompanyLayers();
    },
    mounted() {
        for (const header of this.headers ) {
            header.class='grey lighten-5';
        }
    },
    destroyed(){
        this.refreshCompanyLayers = false;
    },
    methods: {
        async getCompanyLayers() {
            if(this.refreshCompanyLayers){
                if(this.$store.getters.isLoggedIn) {
                    const response = await webtmsBackend.getCompanyLayersEx();
                    this.$store.commit('SET_COMPANY_LAYERS', response.data.companyLayers);
                }
                this.companyLayers = this.$store.getters.companyLayers;
                setTimeout(this.getCompanyLayers, GET_COMPANY_MAP_LAYER_INTERVAL);
            }
        },
        getColor(style) {
            let s = JSON.parse(style);
            if(s.color && s.color.length) {
                if(s.color.startsWith('#') && s.color.length > 7) {
                    return s.color.substring(0, 7);
                }
                return s.color;
            } else {
                return '#808080';
            }
        },
        getShow(style) {
            let s = JSON.parse(style);
            return s.show ? 'Yes' : 'No';

        },
        formatDate(date) {
            return moment(date).format('MM/DD/YYYY');
        },
        setFocusName() {
            this.$refs.shortName.focus();
        },
        cancelAddLayer() {
            this.resetForm();
            this.mapLayerPropertyDialog.visible = false;
        },
        resetForm() {
            console.log('RESET-FORM', this.$refs.editDialogForm);
            this.mapLayerPropertyDialog.mapLayer.layer_id = '';
            this.mapLayerPropertyDialog.mapLayer.name = '';
            this.mapLayerPropertyDialog.mapLayer.description = '';
            this.mapLayerPropertyDialog.mapLayer.style.color = '#3C62DA';
            this.mapLayerPropertyDialog.mapLayer.style.show = false;
            this.mapLayerPropertyDialog.mapLayer.addESRILayer = false;
            this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated = false;
            this.mapLayerPropertyDialog.mapLayer.external_layer_json.url = '';
            this.mapLayerPropertyDialog.mapLayer.external_layer_json.apiKey = '';
            this.mapLayerPropertyDialog.mapLayer.external_layer_json.label = '';
            this.mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields = [{text: '(Do not label)', value: ''}];
            this.mapLayerPropertyDialog.mapLayer.external_layer_json.message = '';
            if(this.$refs.editDialogForm) {
                this.$refs.editDialogForm.resetValidation();
                this.$refs.editDialogForm.validate();
            }
        },
        resetEsriValidation() {
            this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated = false;
            this.mapLayerPropertyDialog.mapLayer.external_layer_json.message = '';
        },
        drawLayer(layer_id) {
            this.$router.push({
                name: 'draw-map-layer',
                params: {
                    id: layer_id
                }
            });
        },
        editLayer(layer_id) {

            let layer = null;
            if(layer_id) {
                layer = this.companyLayers.find(lyr => guidEqual(lyr.layer_id, layer_id));
            }
            this.resetForm();
            if(layer) {
                // Edit existing layer
                this.mapLayerPropertyDialog.mapLayer.layer_id = layer.layer_id;
                this.mapLayerPropertyDialog.mapLayer.name = layer.name;
                this.mapLayerPropertyDialog.mapLayer.description = layer.description;
                this.mapLayerPropertyDialog.mapLayer.style.color = JSON.parse(layer.style).color || '#3C62DA';
                this.mapLayerPropertyDialog.mapLayer.style.show = !!JSON.parse(layer.style).show;
                if(layer.external_layer_json.length) {
                    this.mapLayerPropertyDialog.mapLayer.addESRILayer = true;
                    this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated = false;
                    const lyr = JSON.parse(layer.external_layer_json);
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.url = lyr.url || '';
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.apiKey = lyr.apiKey || '';
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.label = lyr.label || '';
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields = lyr.labelFields || [{text: '(Do not label)', value: ''}];
                } else {
                    this.mapLayerPropertyDialog.mapLayer.addESRILayer = false;
                    this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated = false;
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.url = '';
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.apiKey = '';
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.label = '';
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields = [{text: '(Do not label)', value: ''}];
                }
            } else {
                // Add New Layer
                this.resetForm();
            }
            // Show dialog
            this.mapLayerPropertyDialog.visible = true;
            // set focus to name field
            setTimeout(() => {
                this.setFocusName();
            }, 2);

        },
        async validateEsriLayer() {
            try {
                this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated = false;
                this.mapLayerPropertyDialog.mapLayer.isEsriValidationActive = true;
                this.mapLayerPropertyDialog.mapLayer.external_layer_json.message = '';
                
                let result = await webtmsBackend.validateEsriLayer(this.mapLayerPropertyDialog.mapLayer.external_layer_json.url, this.mapLayerPropertyDialog.mapLayer.external_layer_json.apiKey);
                console.log('RESULT', result);

                if(result && result.isValid) {
                    this.mapLayerPropertyDialog.mapLayer.addESRILayerValidated = true;
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.message = '';
                    // build array of label { text, value } objects for v-select
                    let labels = [];
                    if(result?.labels?.length) {
                        labels = result.labels.map(f => {
                            return {text: f, value: f};
                        });
                    }
                    // add empty label to the beginning of the array
                    labels.unshift({text: '(Do not Label)', value: ''});
                    // if the current label is not in the labels array, reset it to empty string
                    if(this.mapLayerPropertyDialog.mapLayer.external_layer_json.label.length) {
                        if(result.labels.indexOf(this.mapLayerPropertyDialog.mapLayer.external_layer_json.label) === -1) {
                            this.mapLayerPropertyDialog.mapLayer.external_layer_json.label = '';
                        }
                    }
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields = labels;
                } else {
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.message = result?.message || 'An Error Occurred';
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields = undefined;
                    this.mapLayerPropertyDialog.mapLayer.external_layer_json.label = '';
                }
            } catch (error) {
                this.mapLayerPropertyDialog.mapLayer.external_layer_json.message = error.message || 'An Error Occurred';
                this.mapLayerPropertyDialog.mapLayer.external_layer_json.labelFields = undefined;
            } finally {
                this.mapLayerPropertyDialog.mapLayer.isEsriValidationActive = false;
            }

        },
        async UpdateLayer() {
            
            if(this.$refs.editDialogForm.validate()) {
                let result;
                if(this.mapLayerPropertyDialog.mapLayer.layer_id.length) {
                    result = await webtmsBackend.updateMapLayer(this.mapLayerPropertyDialog.mapLayer, this.mapLayerPropertyDialog.mapLayer.addESRILayer);
                } else {
                    result = await webtmsBackend.createMapLayer(this.mapLayerPropertyDialog.mapLayer, this.mapLayerPropertyDialog.mapLayer.addESRILayer);
                }
                if(result && result.cid) {
                    this.getCompanyLayers();
                    this.resetForm();
                    this.mapLayerPropertyDialog.visible = false;
                } else {
                    alert('Failed to save map layer information.');
                    console.err('Failed to save map layer information.' + JSON.stringify(result));
                }
            }
        },
        cancelEditLayer() {
            this.resetForm();
            this.mapLayerPropertyDialog.visible = false;
        },
        cancelDeleteLayer() {
            this.mapLayerDeleteDialog.visible = false;
        },
        deleteLayerDialog(layer_id) {
            let layer = this.companyLayers.find(layer => guidEqual(layer.layer_id, layer_id));
            this.mapLayerDeleteDialog.name = layer.name;
            this.mapLayerDeleteDialog.layer_id = layer.layer_id;
            this.mapLayerDeleteDialog.visible = true;
        },
        async deleteLayer() {
            let result = await webtmsBackend.deleteMapLayer(this.mapLayerDeleteDialog.layer_id);
            if(result.cid) {
                this.getCompanyLayers();
            } else {
                alert(result.err);                
            }
            this.mapLayerDeleteDialog.visible = false;
        },
        uploadLayer(layer_id) {
            this.$router.push({
                name: 'upload-map-layer',
                params: {
                    id: layer_id
                }
            });
        },
        importStatus(status) {
            this.importStatusText = status;
            this.importStatusDialog = true;
        },
        closeStatusDialog() {
            this.importStatusDialog = false;
        }
    }
};
</script>
<style>
.pre {
    white-space: pre-wrap;
    word-wrap:break-word;
}
</style>
<style>
#layer-table thead,
#layer-table th {
    background-color: var(--ll-surface-subtle) !important;
}
</style>
